import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Upload, X, Loader2, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { reportUserFacingError } from '@/lib/errorReporter';

interface ImageUploadProps {
  /** Current image URL */
  value: string;
  /** Called with the new public URL after upload, or empty string on remove */
  onChange: (url: string) => void;
  /** Storage bucket name */
  bucket?: string;
  /** Path prefix within the bucket (e.g. 'avatars', 'logos') */
  pathPrefix?: string;
  /** Accepted file types */
  accept?: string;
  /** Max file size in MB */
  maxSizeMB?: number;
  /** Shape of the preview */
  shape?: 'circle' | 'rounded';
  /** Placeholder icon when no image */
  placeholder?: 'user' | 'company';
  /** Size of the preview in pixels */
  size?: number;
  /** Label text */
  label?: string;
  /** Disabled state */
  disabled?: boolean;
  className?: string;
}

export function ImageUpload({
  value,
  onChange,
  bucket = 'tenant-assets',
  pathPrefix = 'uploads',
  accept = 'image/jpeg,image/png,image/webp,image/gif',
  maxSizeMB = 5,
  shape = 'circle',
  placeholder = 'user',
  size = 96,
  label,
  disabled = false,
  className,
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`Image must be under ${maxSizeMB}MB`);
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${pathPrefix}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      onChange(urlData.publicUrl);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload failed:', error);
      const detail = error instanceof Error ? error.message : 'Unknown error';
      // Surface the real reason to the user AND notify the tech team with a
      // reference (this path previously only showed a generic toast and sent
      // no error report, so upload failures were invisible to us).
      await reportUserFacingError(error, {
        userMessage: `Couldn't upload the image: ${detail}`,
        area: 'image-upload',
        action: `upload:${pathPrefix}`,
        metadata: { bucket, pathPrefix, fileName: file.name, fileType: file.type, fileSize: file.size },
      });
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [bucket, pathPrefix, maxSizeMB, onChange]);

  const handleRemove = useCallback(() => {
    onChange('');
  }, [onChange]);

  const PlaceholderIcon = placeholder === 'company' ? Building2 : User;
  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <p className="text-sm font-medium leading-none">{label}</p>
      )}
      <div className="flex items-center gap-4">
        {/* Preview */}
        <div
          className={cn(
            'relative flex-shrink-0 border-2 border-dashed border-border bg-muted/50 flex items-center justify-center overflow-hidden',
            shapeClass,
          )}
          style={{ width: size, height: size }}
        >
          {value ? (
            <img
              src={value}
              alt="Preview"
              className={cn('w-full h-full object-cover', shapeClass)}
            />
          ) : (
            <PlaceholderIcon className="w-8 h-8 text-muted-foreground/40" />
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
            className="gap-2"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading ? 'Uploading...' : value ? 'Change' : 'Upload'}
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={disabled || uploading}
              className="gap-2 text-destructive hover:text-destructive"
            >
              <X className="w-4 h-4" />
              Remove
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
          />
          <p className="text-xs text-muted-foreground">
            JPG, PNG or WebP. Max {maxSizeMB}MB.
          </p>
        </div>
      </div>
    </div>
  );
}
